/**
 * NonUsWaitlistBanner — Inline banner shown to non-US visitors on the Landing page
 *
 * v1.0 - MKTG-02: Initial implementation - 2026-03-16
 *
 * - NOT a popup, NOT a redirect, does NOT block navigation
 * - Dismiss (X): hides for session only (sessionStorage — reappears next visit)
 * - Submit: POST /api/waitlist/join, source: "non_us_waitlist"
 * - On success: shows confirmation, sets localStorage so banner never re-shows
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import { portalRequestUngated } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company

interface NonUsWaitlistBannerProps {
  detectedCountry: string;
  onDismiss: () => void;
}

const NonUsWaitlistBanner: React.FC<NonUsWaitlistBannerProps> = ({ detectedCountry, onDismiss }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState(detectedCountry || '');
  const [businessType, setBusinessType] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim() || !email.includes('@')) return;
    setLoading(true);
    setError('');
    try {
      const res = await portalRequestUngated('/api/waitlist/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          country: country.trim() || undefined,
          business_type: businessType.trim() || undefined,
          source: 'non_us_waitlist',
        }),
      });
      if (res.ok) {
        // Persist so banner never re-shows after successful submission
        try { localStorage.setItem('mylojic_waitlist_submitted', 'true'); } catch (_) {}
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    // Session-only dismiss — reappears on next visit unless already submitted
    try { sessionStorage.setItem('mylojic_waitlist_dismissed', 'true'); } catch (_) {}
    onDismiss();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'relative',
        p: { xs: 3, sm: 4 },
        borderRadius: 2,
        border: 1,
        borderColor: 'primary.main',
        bgcolor: 'background.paper',
      }}
    >
      {/* Dismiss button */}
      <IconButton
        onClick={handleDismiss}
        size="small"
        aria-label="Dismiss"
        sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <PublicIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
          MyLojic is currently available in the US only
        </Typography>
      </Box>

      {submitted ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          <strong>You're on the list!</strong> We'll be in touch as soon as we launch in your region.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            We're expanding soon. Be the first to know when we launch in your region.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <TextField
              label="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              size="small"
              sx={{ flex: 1 }}
              disabled={loading}
            />
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              size="small"
              sx={{ flex: 1 }}
              disabled={loading}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <TextField
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              disabled={loading}
            />
            <TextField
              label="Type of business (optional)"
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              disabled={loading}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !name.trim() || !email.trim() || !email.includes('@')}
            sx={{ fontWeight: 600, textTransform: 'none', minWidth: 160 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Join the Waitlist'}
          </Button>
        </>
      )}
    </Paper>
  );
};

export default NonUsWaitlistBanner;
